import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { getDefaultBasketObject } from "../../../../shared-logic/utils/constants/BasketConstants";
import type { BasketType } from "../../../../shared-logic/types/BasketType";
import type {
    ColourType,
    ExtraSetItemType,
    ExtraType,
    PackType,
    PriceType,
    ServiceProductType,
    UpholsteryType,
    WheelType,
    WinterWheelType,
} from "../../../../../common-deprecated/types/CommonTypes";
import { find } from "../../../../../common-deprecated/utils";
import type { ConfigurationType } from "../../../../shared-logic/utils/ValidateConfig";

export type CarConfigBasketActionsTypes = ReturnType<
    | typeof initBasket
    | typeof resetBasket
    | typeof setWheels
    | typeof setWinterWheel
    | typeof setColour
    | typeof setUpholstery
    | typeof addPack
    | typeof removePack
    | typeof removeAllPacksAndExtras
    | typeof addPackExtra
    | typeof removePackExtra
    | typeof addExtra
    | typeof removeExtra
    | typeof setExtraOption
    | typeof updateExtraSetItems
    | typeof calculatePrice
    | typeof setConfiguration
    | typeof addServiceProduct
    | typeof removeServiceProduct
    | typeof setMonthlyPrices
>;

export const CarConfigBasketSlice = createSlice({
    name: "basket",
    initialState: getDefaultBasketObject,
    reducers: {
        initBasket(state, action: PayloadAction<BasketType>) {
            return {
                ...state,
                ...action.payload,
                initialized: true,
            };
        },
        resetBasket(state, action: PayloadAction<BasketType>) {
            return {
                ...state,
                ...action.payload,
            };
        },
        setWheels(state, action: PayloadAction<{ wheel: WheelType }>) {
            return {
                ...state,
                wheel: action.payload.wheel,
            };
        },
        setWinterWheel(state, action: PayloadAction<WinterWheelType | null>) {
            return {
                ...state,
                winterWheel: action.payload,
            };
        },
        setColour(state, action: PayloadAction<{ colour: ColourType }>) {
            return {
                ...state,
                colour: action.payload.colour,
            };
        },
        setUpholstery(state, action: PayloadAction<{ upholstery: UpholsteryType }>) {
            return {
                ...state,
                upholstery: action.payload.upholstery,
            };
        },
        addPack(
            state,
            action: PayloadAction<{
                pack: PackType;
                extra?: ExtraType;
                selectedExtras: ExtraType[];
                selectedPacks: PackType[];
                automatic: boolean;
            }>,
        ) {
            return {
                ...state,
                packs: !find(action.payload.pack.id, state.packs) ? [...state.packs, action.payload.pack] : state.packs,
            };
        },
        removePack(state, action: PayloadAction<{ pack: PackType }>) {
            return {
                ...state,
                packs: state.packs.filter((pack) => pack.id !== action.payload.pack.id),
            };
        },
        removeAllPacksAndExtras(state) {
            return {
                ...state,
                packs: [],
                extras: [],
            };
        },
        resolvePackConflict(
            state,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            action: PayloadAction<{
                pack: PackType;
                selectedItem: ExtraType | PackType;
                conflicts: PackType[] | ExtraType[];
                selectedConflicts: ExtraType[] | PackType[];
                isConflictBetweenPackExtras: boolean;
            }>,
        ) {
            return {
                ...state,
            };
        },
        addPackExtra(
            state,
            action: PayloadAction<{
                pack: PackType;
                extra: ExtraType;
                automatic: boolean;
                selectedExtras: ExtraType[];
                combinedSelectedExtras?: ExtraType[];
            }>,
        ) {
            const packExtras = action.payload.combinedSelectedExtras
                ? action.payload.combinedSelectedExtras.reduce(
                      (extrasArray, packExtra) => {
                          if (!find(packExtra.id, extrasArray)) extrasArray.push(packExtra);
                          return extrasArray;
                      },
                      [...state.packExtras],
                  )
                : state.packExtras;

            return {
                ...state,
                packs: !find(action.payload.pack.id, state.packs) ? [...state.packs, action.payload.pack] : state.packs,
                packExtras,
            };
        },
        removePackExtra(state, action: PayloadAction<{ extra: ExtraType }>) {
            return {
                ...state,
                packExtras: state.packExtras.filter((packExtra) => packExtra.id !== action.payload.extra.id),
            };
        },
        addExtra(
            state,
            action: PayloadAction<{
                extra: ExtraType;
                automatic: boolean;
                setItems?: ExtraSetItemType[];
            }>,
        ) {
            const { extra, automatic, setItems = [] } = action.payload;
            return {
                ...state,
                extras: [
                    ...state.extras,
                    {
                        ...extra,
                        userSelected: !automatic,
                        setItems,
                    },
                ],
            };
        },
        removeExtra(state, action: PayloadAction<{ extra: ExtraType }>) {
            return {
                ...state,
                extras: state.extras.filter((extra) => extra.id !== action.payload.extra.id),
            };
        },
        setExtraOption(state, action: PayloadAction<{ extra: ExtraType; optionId: string }>) {
            const extraIndex = state.extras.findIndex((extra) => extra.id === action.payload.extra.id);
            const extra = { ...state.extras[extraIndex], selectedOptionId: action.payload.optionId };
            const newExtras = [...state.extras];
            newExtras[extraIndex] = extra;

            return {
                ...state,
                extras: newExtras,
            };
        },
        updateExtraSetItems(state, action: PayloadAction<{ extra: ExtraType; setItems: ExtraSetItemType[] }>) {
            const extraIndex = state.extras.findIndex((extra) => extra.id === action.payload.extra.id);
            const extras = [...state.extras];
            extras.splice(extraIndex, 1, { ...action.payload.extra, setItems: action.payload.setItems });
            return {
                ...state,
                extras,
            };
        },
        calculatePrice(
            state,
            action: PayloadAction<{
                total?: PriceType | undefined;
                loadExternalPrice?: boolean;
                firstTime?: boolean;
                previousTotal?: PriceType;
            }>,
        ) {
            return {
                ...state,
                total: action.payload.total as BasketType["total"],
            };
        },
        setConfiguration(state, action: PayloadAction<ConfigurationType>) {
            return {
                ...state,
                ...action.payload,
            };
        },
        addServiceProduct(state, action: PayloadAction<{ serviceProduct: ServiceProductType }>) {
            return {
                ...state,
                serviceProducts: !find(action.payload.serviceProduct.id, state.serviceProducts)
                    ? [...state.serviceProducts, action.payload.serviceProduct]
                    : state.serviceProducts,
            };
        },
        removeServiceProduct(state, action: PayloadAction<{ serviceProduct: ServiceProductType }>) {
            return {
                ...state,
                serviceProducts: state.serviceProducts.filter(
                    (serviceProduct) => serviceProduct.id !== action.payload.serviceProduct.id,
                ),
            };
        },
        setMonthlyPrices(state, action: PayloadAction<Record<string, string>>) {
            const { colour, extras, packs, upholstery, wheel } = state;
            // Apply monthly rates for items
            [colour, ...extras, ...packs, upholstery, wheel].map((item) => {
                if (item.price) {
                    item.price.monthly = Number(action.payload[item.id] ?? 0);
                }

                return item;
            });
            return state;
        },
    },
});

export const {
    initBasket,
    resetBasket,
    setWheels,
    setWinterWheel,
    setColour,
    addPack,
    removePack,
    removeAllPacksAndExtras,
    resolvePackConflict,
    addPackExtra,
    removePackExtra,
    addExtra,
    removeExtra,
    setExtraOption,
    updateExtraSetItems,
    calculatePrice,
    setUpholstery,
    setConfiguration,
    addServiceProduct,
    removeServiceProduct,
    setMonthlyPrices,
} = CarConfigBasketSlice.actions;
